"use client";

import { ArrowLeft, ExternalLink, Github } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { notFound, useParams } from "next/navigation";

import ImageWithFallback from "@/components/image-with-fallback";
import { projects } from "@/features/profile/data/projects";

// Helper function to show icon based on technology name
function getTechIcon(tech: string) {
  const lower = tech.toLowerCase();
  const iconMap: Record<string, string> = {
    react: "1174949_js_react js_logo_react_react native_icon.png",
    javascript: "icons8-javascript-48.png",
    js: "icons8-javascript-48.png",
    typescript: "icons8-typescript-50.png",
    ts: "icons8-typescript-50.png",
    next: "icons8-next.js-48.png",
    nextjs: "icons8-next.js-48.png",
    node: "icons8-nodejs-50.png",
    nodejs: "icons8-nodejs-50.png",
    github: "github(2).png",
    shadcn: "shadcn-ui.png",
    "shadcn/ui": "shadcn-ui.png",
    java: "icons8-java-logo-94.png",
    spring: "icons8-spring-boot-48.png",
    springboot: "icons8-spring-boot-48.png",
    "spring boot": "icons8-spring-boot-48.png",
    tailwind: "icons8-tailwind-css-48.png",
    tailwindcss: "icons8-tailwind-css-48.png",
  };
  const fileName =
    iconMap[lower] || iconMap[lower.replace(/[^a-z0-9]/g, "")] || null;
  return fileName ? (
    <span className="flex h-full w-full items-center justify-center">
      <Image
        src={`/projectstack/${fileName}`}
        alt={tech}
        width={20}
        height={20}
        className="h-5 w-5 object-contain"
        unoptimized
      />
    </span>
  ) : (
    <span className="text-xs font-semibold text-gray-500 dark:text-gray-300">
      {tech[0].toUpperCase()}
    </span>
  );
}

export default function ProjectDetailPage() {
  const params = useParams();
  const slug = params?.slug as string;

  const project = projects.find((p) => p.slug === slug);

  if (!project) {
    notFound();
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 dark:from-gray-900 dark:to-gray-950">
      <div className="container mx-auto px-4">
        <Link
          href="/projects"
          className="mb-8 inline-flex items-center text-purple-600 transition-transform hover:translate-x-[-2px] hover:underline dark:text-purple-400"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to all projects
        </Link>

        <div className="perspective-1200 mx-auto max-w-4xl">
          <div className="mb-8 transform overflow-hidden rounded-xl bg-gradient-to-br from-purple-200 via-pink-200 to-purple-300 shadow-lg transition-transform duration-500 hover:[transform:rotateX(2deg)_rotateY(-1deg)_translateZ(10px)] hover:shadow-2xl dark:from-purple-900 dark:via-pink-900 dark:to-purple-800">
            <div className="relative aspect-[16/9] w-full overflow-hidden">
              {"video" in project && project.video ? (
                <video
                  className="h-full w-full object-cover transition-transform duration-700 ease-in-out hover:scale-105"
                  src={project.video as string}
                  poster={project.image}
                  muted
                  playsInline
                  autoPlay
                  loop
                />
              ) : (
                <ImageWithFallback
                  src={project.image}
                  alt={project.name}
                  className="object-cover transition-transform duration-700 ease-in-out hover:scale-105"
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 60vw"
                  priority
                />
              )}
            </div>
          </div>

          <div className="rounded-xl bg-white p-8 shadow-lg dark:bg-gray-800">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <h1 className="text-4xl font-bold dark:text-white">
                {project.name}
              </h1>

              <div className="flex items-center gap-3">
                {project.liveDemo && (
                  <a
                    href={project.liveDemo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Live Demo
                  </a>
                )}
                <a
                  href={project.sourceCode}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center rounded-md bg-gray-600 px-4 py-2 text-white transition-colors hover:bg-gray-700"
                >
                  <Github className="mr-2 h-4 w-4" />
                  Source Code
                </a>
              </div>
            </div>

            <div className="mb-6 flex items-center gap-3">
              <span
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                  project.status === "In Progress"
                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                    : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                }`}
              >
                <span
                  className={`mr-1.5 h-2 w-2 rounded-full ${
                    project.status === "In Progress"
                      ? "bg-yellow-600"
                      : "bg-green-600"
                  }`}
                ></span>
                {project.status}
              </span>
            </div>

            <div className="prose mb-8 max-w-none dark:prose-invert">
              <p className="text-lg">{project.description}</p>
            </div>

            <div className="mb-8">
              <h2 className="mb-4 text-xl font-bold dark:text-white">
                Technology Stack
              </h2>
              <div className="flex flex-wrap gap-3">
                {project.techStack.map((tech) => (
                  <div
                    key={tech}
                    className="flex items-center gap-2.5 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium transition-all duration-300 hover:bg-gray-200 hover:shadow-md dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    <span className="flex h-6 w-6 items-center justify-center">
                      {getTechIcon(tech)}
                    </span>
                    {tech}
                  </div>
                ))}
              </div>
            </div>

            {/* Project Details Section */}
            <div className="border-t border-gray-200 pt-8 dark:border-gray-700">
              <h2 className="mb-6 text-2xl font-bold dark:text-white">
                About This Project
              </h2>
              <div className="prose max-w-none dark:prose-invert">
                <p>
                  This is where you could add more detailed information about
                  the project, such as the challenges you faced, what you
                  learned, and how you solved problems.
                </p>
                <p>You could also include information about:</p>
                <ul>
                  <li>The problem the project solves</li>
                  <li>Key features and functionality</li>
                  <li>Technical challenges overcome</li>
                  <li>Your role in the project</li>
                  <li>Future enhancements planned</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
